import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Crown,
  Database,
  Flame,
  LineChart,
  Mail,
  MessageCircle,
  PhoneCall,
  Sparkles,
  Wand2,
  Workflow,
} from 'lucide-react';

const WHATSAPP_RENACIMIENTO_URL =
  'https://api.whatsapp.com/send?phone=13057075219&text=Hola%2C%20me%20interesa%20el%20pack%20Renacimiento%20de%20automatizaci%C3%B3n%20CRM%20o%20Procesos';

const PageHeader = () => {
  const { t } = useTranslation();
  return (
    <header className="fixed top-0 w-full z-50 border-b border-white/10 bg-black/70 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
        <a href="/" className="flex items-center gap-3">
          <img
            src="https://imgur.com/wDWVbR4.png"
            alt="Nextcoders"
            className="h-8"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/150x32/0b0b0b/d4af37?text=Nextcoders';
            }}
          />
          <span className="hidden sm:inline text-xs tracking-[0.35em] uppercase text-white/70">{t('common.exclusive_edition')}</span>
        </a>
        <div className="flex items-center gap-4 md:gap-8">
          <LanguageSwitcher variant="compact" />
          <a
            href={WHATSAPP_RENACIMIENTO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#d4af37] text-black px-4 md:px-6 py-2 rounded-lg hover:bg-[#e1c05a] transition text-sm font-semibold"
          >
            {t('common.contact_advisor')}
          </a>
        </div>
      </div>
    </header>
  );
};

const useInView = (ref, { threshold = 0.25, rootMargin = '0px' } = {}) => {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold, rootMargin }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, threshold, rootMargin]);

  return inView;
};

const useCountUp = (enabled, toValue, durationMs = 900) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let rafId = 0;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * toValue));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [enabled, toValue, durationMs]);

  return value;
};

const StatCard = ({ label, value, suffix, tone }) => (
  <div
    className={`rounded-2xl border backdrop-blur p-5 transition ${
      tone === 'gold'
        ? 'border-[#d4af37]/25 bg-black/35 hover:border-[#d4af37]/45 hover:bg-black/45'
        : 'border-white/10 bg-white/5 hover:border-[#d4af37]/25 hover:bg-white/10'
    }`}
  >
    <div className="text-xs uppercase tracking-[0.25em] text-white/60">{label}</div>
    <div className="mt-3 font-serif text-3xl md:text-4xl text-white">
      {value}
      {suffix}
    </div>
  </div>
);

const LuxeImageSlot = ({ src, alt, label, hint, aspect = 'aspect-[16/9]' }) => (
  <div className="group relative rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(50%_60%_at_50%_0%,rgba(212,175,55,0.18),rgba(0,0,0,0)_70%)] opacity-0 group-hover:opacity-100 transition-opacity" />
    <div className={`relative ${aspect} overflow-hidden`}>
      <img
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 transition duration-700 group-hover:scale-[1.03]"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = 'https://placehold.co/1200x675/0b0b0b/d4af37?text=Imagen';
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="absolute -left-1/2 top-0 bottom-0 w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-[-20deg] animate-[shimmer_2.2s_ease-in-out_infinite]" />
      </div>
    </div>
    <div className="relative p-5">
      <div className="text-xs uppercase tracking-[0.35em] text-white/60">{label}</div>
      <div className="mt-2 text-white/80 text-sm leading-relaxed">{hint}</div>
    </div>
    <style>{`@keyframes shimmer{0%{transform:translateX(-35%)}100%{transform:translateX(165%)}}`}</style>
  </div>
);

const ScrollReveal = ({ children, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.18, rootMargin: '120px' });
  return (
    <div
      ref={ref}
      className={`transition duration-700 will-change-transform ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      } ${className}`}
    >
      {children}
    </div>
  );
};

const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel = 'Antes',
  afterLabel = 'Después',
  comparisonLabel,
  title,
  dragHint,
  ariaLabel,
}) => {
  const [value, setValue] = useState(55);
  return (
    <div className="group rounded-2xl border border-white/10 bg-white/5 overflow-hidden transition hover:border-[#d4af37]/25 hover:bg-white/10">
      <div className="px-7 py-5 border-b border-white/10 flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-white/60">{comparisonLabel}</div>
          <div className="mt-2 font-serif text-2xl">{title}</div>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-white/60">
          <span className="text-white/70">{beforeLabel}</span>
          <span className="text-[#d4af37]">↔</span>
          <span className="text-white/90">{afterLabel}</span>
        </div>
      </div>

      <div className="p-6">
        <div className="relative aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 bg-black">
          <img
            src={beforeSrc}
            alt={beforeLabel}
            className="absolute inset-0 w-full h-full object-cover opacity-85"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://placehold.co/1600x900/0b0b0b/ffffff?text=Antes';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/15 to-transparent" />

          <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - value}% 0 0)` }}>
            <img
              src={afterSrc}
              alt={afterLabel}
              className="absolute inset-0 w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://placehold.co/1600x900/0b0b0b/d4af37?text=Despues';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-l from-black/45 via-black/10 to-transparent" />
          </div>

          <div className="absolute inset-y-0" style={{ left: `${value}%` }}>
            <div className="absolute -left-px top-0 bottom-0 w-[2px] bg-[#d4af37]" />
            <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2">
              <div className="w-12 h-12 rounded-full bg-black/70 border border-[#d4af37]/50 backdrop-blur flex items-center justify-center shadow-[0_0_0_8px_rgba(212,175,55,0.06)] transition group-hover:scale-[1.03]">
                <span className="text-[#d4af37] text-xs tracking-[0.25em]">|||</span>
              </div>
            </div>
          </div>

          <input
            type="range"
            min={10}
            max={90}
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            aria-label={ariaLabel}
            className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
          />

          <div className="absolute left-5 top-5 text-xs uppercase tracking-[0.35em] text-white/80">{beforeLabel}</div>
          <div className="absolute right-5 top-5 text-xs uppercase tracking-[0.35em] text-[#d4af37]">{afterLabel}</div>
        </div>

        <div className="mt-5 text-white/60 text-sm">
          {dragHint}
        </div>
      </div>
    </div>
  );
};

const TransformationDiagram = ({ tag, title, sourceNodes, coreTitle, coreSubtitle, outputNodes, footerCards }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.25, rootMargin: '140px' });
  const dashOffset = inView ? 0 : 1400;

  return (
    <div ref={ref} className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
      <div className="px-7 py-5 border-b border-white/10">
        <div className="text-xs uppercase tracking-[0.35em] text-white/60">{tag}</div>
        <div className="mt-2 font-serif text-2xl">{title}</div>
      </div>
      <div className="p-6">
        <div className="rounded-2xl border border-white/10 bg-black/40 overflow-hidden">
          <svg viewBox="0 0 900 300" className="w-full h-auto">
            <defs>
              <linearGradient id="goldLine" x1="0" x2="1">
                <stop offset="0%" stopColor="rgba(212,175,55,0.15)" />
                <stop offset="45%" stopColor="rgba(212,175,55,0.7)" />
                <stop offset="100%" stopColor="rgba(212,175,55,0.15)" />
              </linearGradient>
              <radialGradient id="glow" cx="50%" cy="50%" r="60%">
                <stop offset="0%" stopColor="rgba(212,175,55,0.35)" />
                <stop offset="100%" stopColor="rgba(0,0,0,0)" />
              </radialGradient>
            </defs>

            <rect x="0" y="0" width="900" height="300" fill="rgba(0,0,0,0.4)" />

            <g opacity="0.95">
              {sourceNodes.map((n) => (
                <g key={n.label}>
                  <rect x={n.x - 70} y={n.y - 22} rx="14" ry="14" width="140" height="44" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.10)" />
                  <text x={n.x} y={n.y + 5} textAnchor="middle" fill="rgba(255,255,255,0.78)" fontSize="14" fontFamily="ui-sans-serif, system-ui">
                    {n.label}
                  </text>
                </g>
              ))}
            </g>

            <g>
              <circle cx="470" cy="150" r="86" fill="url(#glow)" />
              <rect x="380" y="112" rx="18" ry="18" width="180" height="76" fill="rgba(212,175,55,0.10)" stroke="rgba(212,175,55,0.35)" />
              <text x="470" y="148" textAnchor="middle" fill="rgba(255,255,255,0.92)" fontSize="16" fontFamily="ui-serif, Georgia">
                {coreTitle}
              </text>
              <text x="470" y="168" textAnchor="middle" fill="rgba(255,255,255,0.66)" fontSize="12" fontFamily="ui-sans-serif, system-ui">
                {coreSubtitle}
              </text>
            </g>

            <g>
              {outputNodes.map((n) => (
                <g key={n.label}>
                  <rect x={n.x - 78} y={n.y - 22} rx="14" ry="14" width="156" height="44" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.10)" />
                  <text x={n.x} y={n.y + 5} textAnchor="middle" fill="rgba(255,255,255,0.80)" fontSize="14" fontFamily="ui-sans-serif, system-ui">
                    {n.label}
                  </text>
                </g>
              ))}
            </g>

            <g
              fill="none"
              stroke="url(#goldLine)"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeDasharray="10 10"
              style={{
                strokeDashoffset: dashOffset,
                transition: 'stroke-dashoffset 1400ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              <path d="M190 70 C 260 70, 310 90, 380 128" />
              <path d="M190 140 C 260 140, 310 140, 380 150" />
              <path d="M190 210 C 260 210, 310 190, 380 172" />
              <path d="M370 100 C 400 110, 410 125, 390 136" opacity="0.55" />
              <path d="M370 180 C 400 170, 410 165, 390 156" opacity="0.55" />

              <path d="M560 140 C 630 120, 660 108, 662 95" />
              <path d="M560 150 C 640 150, 662 155, 662 155" />
              <path d="M560 160 C 630 178, 660 196, 662 215" />
            </g>
          </svg>
        </div>

        <div className="mt-5 grid sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white/75 flex items-center gap-3">
            <MessageCircle className="w-4 h-4 text-white/60" />
            {footerCards[0]}
          </div>
          <div className="rounded-xl border border-[#d4af37]/25 bg-black/35 px-4 py-3 text-white/80 flex items-center gap-3">
            <Database className="w-4 h-4 text-[#d4af37]" />
            {footerCards[1]}
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white/75 flex items-center gap-3">
            <LineChart className="w-4 h-4 text-white/60" />
            {footerCards[2]}
          </div>
        </div>
      </div>
    </div>
  );
};

const MaturityBar = ({ className = '', titleTag, title, items, actionsTitle, actionsDesc, actionChips }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.35, rootMargin: '140px' });
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!inView) return;
    setActive(0);
    let step = 0;
    const id = setInterval(() => {
      step += 1;
      setActive(step);
      if (step >= 5) clearInterval(id);
    }, 240);
    return () => clearInterval(id);
  }, [inView]);

  return (
    <div
      ref={ref}
      className={`rounded-2xl border border-white/10 bg-white/5 p-7 flex flex-col transition hover:border-[#d4af37]/25 hover:bg-white/10 ${className}`}
    >
      <div className="flex items-center justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-white/60">{titleTag}</div>
          <div className="mt-2 font-serif text-xl">{title}</div>
        </div>
        <div className="text-xs uppercase tracking-[0.35em] text-white/60">{Math.max(active, 1)}/5</div>
      </div>
      <div className="mt-6 grid grid-cols-5 gap-2">
        {Array.from({ length: 5 }).map((_, i) => {
          const on = i < active;
          return (
            <div
              key={i}
              className={`h-2 rounded-full transition ${
                on ? 'bg-[#d4af37]' : 'bg-white/10'
              }`}
            />
          );
        })}
      </div>
      <div className="mt-5 grid gap-3 text-sm flex-1">
        {items.map((text, idx) => (
          <div
            key={text}
            className={`rounded-xl border px-4 py-3 flex items-center gap-3 transition hover:-translate-y-0.5 hover:bg-white/5 ${
              idx < active ? 'border-[#d4af37]/25 bg-black/35' : 'border-white/10 bg-black/20'
            }`}
          >
            {idx < active ? <CheckCircle2 className="w-4 h-4 text-[#d4af37]" /> : <AlertTriangle className="w-4 h-4 text-white/60" />}
            <span className="text-white/75">{text}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-xl border border-white/10 bg-black/30 p-5">
        <div className="text-xs uppercase tracking-[0.25em] text-white/60">{actionsTitle}</div>
        <div className="mt-2 text-white/70 text-sm leading-relaxed">
          {actionsDesc}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {actionChips.map((chip) => (
            <div
              key={chip}
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/75 transition hover:border-[#d4af37]/25 hover:bg-black/35"
            >
              {chip}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RenacimientoPage = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
  const c = {
    es: {
      editorialLine: 'Linea de transformacion editorial',
      comparisonLabel: 'Comparacion',
      comparisonTitle: 'Antes vs Despues del Renacimiento',
      stageLabel: 'Etapa',
      beforeAfterDrag: 'Arrastra horizontalmente para revelar la transicion.',
      beforeAfterAria: 'Deslizar comparacion antes y despues',
      beforeTitle: 'Operacion en penumbra',
      afterTitle: 'Claridad y control',
      beforeList: ['Informacion dispersa', 'Dependencia de personas', 'Errores manuales', 'Decisiones lentas', 'Crecimiento desordenado'],
      afterList: ['Informacion centralizada', 'Procesos automatizados', 'Trazabilidad total', 'Decisiones rapidas', 'Escalabilidad controlada'],
      transformationTag: 'Transformacion',
      transformationTitle: 'Del caos al nucleo',
      transformationSources: ['WhatsApp', 'Excel', 'Correo', 'Llamadas', 'Silos'],
      transformationCoreTitle: 'CRM / Plataforma',
      transformationCoreSubtitle: 'nucleo central',
      transformationOutputs: ['KPIs', 'Alertas', 'Trazabilidad'],
      transformationFooters: ['Bloques desconectados', 'Nucleo central', 'Decision con datos'],
      maturityTag: 'Madurez',
      maturityTitle: 'Senales de falsa digitalizacion',
      maturityItems: ['Sistemas que no se hablan entre si', 'Doble o triple carga de informacion', 'Reportes manuales', 'Automatizaciones sin logica de negocio', 'Tecnologia que estorba en vez de ayudar'],
      maturityActionsTitle: 'Acciones inmediatas',
      maturityActionsDesc: 'Pasa el mouse por cada senal y detecta donde integrar, automatizar y medir primero.',
      maturityActionChips: ['Integrar sistemas', 'Eliminar doble carga', 'Automatizar reportes', 'Conectar KPIs'],
      supportImage1Alt: 'Construye bases seguras',
      supportImage1Label: 'Construye bases seguras',
      supportImage1Hint: 'La fortaleza de una organizacion no es el marketing, si no, lo que realmente hay detras de la operacion, orden o sobre costos.',
      supportImage2Alt: 'Sufres los sintomas del caos',
      supportImage2Label: 'Sufres los sintomas del caos',
      supportImage2Hint: 'No basta con tener el control, tu equipo avanza al ritmo del desorden, la escasa trazabilidad y el retrabajo constante.',
      diagnosisImageAlt: 'Palabras que ordenan procesos',
      diagnosisImageLabel: 'Palabras que ordenan procesos',
      diagnosisImageHint: 'Reunirse con el equipo para ajustar y no para arreglar es algo que muy pocas empresas hacen.',
      casesTag: 'Casos reales',
      casesTitle: 'Resultados medibles del renacer',
      casesDesc: 'No se trata de promesas. Se trata de impacto en conversion, tiempos, reclamos y experiencia.',
      casesHint: 'KPIs de ejemplo basados en implementacion guiada',
      case1: 'Empresa B2B de servicios',
      case2: 'Alto volumen de soporte',
      before: 'Antes',
      after: 'Despues',
      case1Before: ['Ventas por WhatsApp personal', 'Seguimiento manual'],
      case1After: ['CRM + automatizaciones', 'Seguimiento automatico', 'KPIs'],
      case2Before: ['Tickets perdidos', 'Reclamos repetidos'],
      case2After: ['Contact Center integrado', 'Historial unico', 'KPIs'],
      case1Tag: 'Caso 1',
      case2Tag: 'Caso 2',
      case1ImageAlt: 'Sugerencia de imagen Caso 1',
      case2ImageAlt: 'Sugerencia de imagen Caso 2',
      case1ConversionLabel: 'Conversion',
      case1CloseTimeLabel: 'Tiempo de cierre',
      case2ClaimsLabel: 'Reclamos',
      case2CsatLabel: 'CSAT',
      stages: [
        {
          title: 'Despertar',
          subtitle: 'Auditoria de procesos',
          points: ['Ventas', 'Marketing', 'Atencion al cliente', 'Operaciones', 'Direccion'],
          note: 'Nada teorico. Todo real.',
          visualAlt: 'Imagen sugerida para Despertar',
          visualHint: 'Mapa de procesos en papel + escritorio premium, luz calida.',
        },
        {
          title: 'Purificacion',
          subtitle: 'Eliminacion de friccion',
          points: ['Procesos redundantes', 'Pasos manuales innecesarios', 'Dependencias criticas'],
          note: 'Aqui muere lo que no aporta valor.',
          visualAlt: 'Imagen sugerida para Purificacion',
          visualHint: 'Checklist minimalista, resaltado dorado, foco en eliminar.',
        },
        {
          title: 'Reconstruccion',
          subtitle: 'Estrategia',
          points: ['CRM', 'Automatizaciones', 'Flujos inteligentes', 'Canales centralizados'],
          note: 'No se digitaliza todo. Se digitaliza lo que impacta resultados.',
          visualAlt: 'Imagen sugerida para Reconstruccion',
          visualHint: 'Diagrama de flujo limpio conectando canales a un nucleo.',
        },
        {
          title: 'Iluminacion',
          subtitle: 'Analitica y control',
          points: ['KPIs claros', 'Dashboards en tiempo real', 'Alertas automaticas'],
          note: 'La empresa deja de sentir y empieza a ver.',
          visualAlt: 'Imagen sugerida para Iluminacion',
          visualHint: 'Dashboard oscuro premium con graficas doradas y alertas sutiles.',
        },
        {
          title: 'Renacer',
          subtitle: 'Mejora continua',
          points: ['Procesos vivos', 'Automatizaciones evolutivas', 'Decisiones basadas en datos'],
          note: 'La empresa renace preparada para crecer.',
          visualAlt: 'Imagen sugerida para Renacer',
          visualHint: 'Luz ascendente, skyline minimal, sensacion de expansion controlada.',
        },
      ],
      benefitsTag: 'Beneficios',
      benefitsTitle: 'Lo exclusivo no es el software. Es el control.',
      benefitsDesc: 'Control total del negocio, menos dependencia operativa, mas productividad sin mas personas, datos confiables, escalabilidad real e imagen corporativa moderna y solida.',
      benefitsList: ['Control total del negocio', 'Menos dependencia operativa', 'Mas productividad sin mas personas', 'Datos confiables', 'Escalabilidad real', 'Imagen corporativa moderna y solida'],
      benefitsAnalyticsAlt: 'Analitica',
      benefitsAnalyticsLabel: 'Analitica',
      benefitsAnalyticsHint: 'Dashboards con informacion confiable.',
      benefitsImprovementAlt: 'Mejora continua',
      benefitsImprovementLabel: 'Mejora continua',
      benefitsImprovementHint: 'La evolucion de tu empresa y procesos.',
      benefitsTimeAlt: 'El tiempo es oro',
      benefitsTimeLabel: 'El tiempo es oro',
      benefitsTimeHint: 'Invierte en tecnologia que elimine la merma laboral.',
      promoTag: 'Promocion',
      promoTitle: 'Paquete exclusivo Renacer',
      promoImageLabel: 'Aqui renace tu pasion por el exito',
      promoImageHint: 'Dale a tu empresa el motor que consiga los resultados con los que hoy suenas.',
      promoList: ['Auditoria completa de procesos', 'Software de gestion (CRM / Automatizacion)', 'Diseno de flujos personalizados', 'Implementacion guiada', 'Capacitacion inicial'],
      conditionTag: 'Condicion especial',
      conditionDesc: 'Un solo paquete. Un solo proceso. Un solo objetivo: que tu empresa renazca.',
      whyNowTag: 'Por que ahora',
      whyNowTitle: 'Renacer no es opcional',
      whyNowDesc: 'Las empresas del futuro no desplazaran a las tradicionales. Las empresas digitalizadas desplazaran a las que no comenzaron hoy. Renacer es una decision estrategica.',
      finalTitle: 'Es momento de Renacer',
      finalDesc: 'Solicita tu diagnostico Renacimiento y descubre como transformar tu empresa desde la raiz.',
      finalLine: 'Cuando la tecnologia deja de ser un gasto y se convierte en poder.',
      contactAdvisor: 'Contactar con un asesor',
    },
    en: {
      editorialLine: 'Editorial transformation line',
      comparisonLabel: 'Comparison',
      comparisonTitle: 'Before vs After Renaissance',
      stageLabel: 'Stage',
      beforeAfterDrag: 'Drag horizontally to reveal the transition.',
      beforeAfterAria: 'Slide before and after comparison',
      beforeTitle: 'Operation in the shadows',
      afterTitle: 'Clarity and control',
      beforeList: ['Scattered information', 'People dependency', 'Manual errors', 'Slow decisions', 'Disordered growth'],
      afterList: ['Centralized information', 'Automated processes', 'Full traceability', 'Faster decisions', 'Controlled scalability'],
      transformationTag: 'Transformation',
      transformationTitle: 'From chaos to the core',
      transformationSources: ['WhatsApp', 'Excel', 'Email', 'Calls', 'Silos'],
      transformationCoreTitle: 'CRM / Platform',
      transformationCoreSubtitle: 'central core',
      transformationOutputs: ['KPIs', 'Alerts', 'Traceability'],
      transformationFooters: ['Disconnected blocks', 'Central core', 'Data-driven decisions'],
      maturityTag: 'Maturity',
      maturityTitle: 'Signs of false digitalization',
      maturityItems: ['Systems that do not talk to each other', 'Double or triple data entry', 'Manual reports', 'Automations without business logic', 'Technology that gets in the way instead of helping'],
      maturityActionsTitle: 'Immediate actions',
      maturityActionsDesc: 'Hover each sign and detect where to integrate, automate, and measure first.',
      maturityActionChips: ['Integrate systems', 'Eliminate duplicate work', 'Automate reports', 'Connect KPIs'],
      supportImage1Alt: 'Build secure foundations',
      supportImage1Label: 'Build secure foundations',
      supportImage1Hint: 'The strength of an organization is not marketing, but what truly supports the operation, order, and cost control.',
      supportImage2Alt: 'Do you suffer the symptoms of chaos',
      supportImage2Label: 'Do you suffer the symptoms of chaos',
      supportImage2Hint: 'Having control is not enough if your team still moves at the pace of disorder, low traceability, and constant rework.',
      diagnosisImageAlt: 'Words that organize processes',
      diagnosisImageLabel: 'Words that organize processes',
      diagnosisImageHint: 'Meeting with the team to refine instead of constantly fixing is something very few companies actually do.',
      casesTag: 'Real cases',
      casesTitle: 'Measurable rebirth results',
      casesDesc: 'These are not promises. This is impact on conversion, time, claims, and experience.',
      casesHint: 'Sample KPIs based on guided implementation',
      case1: 'B2B services company',
      case2: 'High support volume',
      before: 'Before',
      after: 'After',
      case1Before: ['Sales via personal WhatsApp', 'Manual follow-up'],
      case1After: ['CRM + automation', 'Automatic follow-up', 'KPIs'],
      case2Before: ['Lost tickets', 'Repeated claims'],
      case2After: ['Integrated Contact Center', 'Single customer history', 'KPIs'],
      case1Tag: 'Case 1',
      case2Tag: 'Case 2',
      case1ImageAlt: 'Suggested image Case 1',
      case2ImageAlt: 'Suggested image Case 2',
      case1ConversionLabel: 'Conversion',
      case1CloseTimeLabel: 'Closing time',
      case2ClaimsLabel: 'Claims',
      case2CsatLabel: 'CSAT',
      stages: [
        {
          title: 'Awakening',
          subtitle: 'Process audit',
          points: ['Sales', 'Marketing', 'Customer service', 'Operations', 'Management'],
          note: 'Nothing theoretical. Everything real.',
          visualAlt: 'Suggested image for Awakening',
          visualHint: 'Process map on paper with a premium desk and warm lighting.',
        },
        {
          title: 'Purification',
          subtitle: 'Friction removal',
          points: ['Redundant processes', 'Unnecessary manual steps', 'Critical dependencies'],
          note: 'This is where what adds no value dies.',
          visualAlt: 'Suggested image for Purification',
          visualHint: 'Minimal checklist, golden highlights, focus on elimination.',
        },
        {
          title: 'Reconstruction',
          subtitle: 'Strategy',
          points: ['CRM', 'Automations', 'Smart flows', 'Centralized channels'],
          note: 'Not everything gets digitized. Only what impacts results.',
          visualAlt: 'Suggested image for Reconstruction',
          visualHint: 'Clean flow diagram connecting channels to a central core.',
        },
        {
          title: 'Illumination',
          subtitle: 'Analytics and control',
          points: ['Clear KPIs', 'Real-time dashboards', 'Automatic alerts'],
          note: 'The company stops feeling and starts seeing.',
          visualAlt: 'Suggested image for Illumination',
          visualHint: 'Premium dark dashboard with golden charts and subtle alerts.',
        },
        {
          title: 'Rebirth',
          subtitle: 'Continuous improvement',
          points: ['Living processes', 'Evolving automations', 'Data-based decisions'],
          note: 'The company is reborn ready to grow.',
          visualAlt: 'Suggested image for Rebirth',
          visualHint: 'Ascending light, minimal skyline, feeling of controlled expansion.',
        },
      ],
      benefitsTag: 'Benefits',
      benefitsTitle: 'What is exclusive is not software. It is control.',
      benefitsDesc: 'Total business control, less operational dependency, more productivity without extra headcount, reliable data, real scalability, and a strong modern brand image.',
      benefitsList: ['Total business control', 'Less operational dependency', 'More productivity without extra headcount', 'Reliable data', 'Real scalability', 'Modern and solid brand image'],
      benefitsAnalyticsAlt: 'Analytics',
      benefitsAnalyticsLabel: 'Analytics',
      benefitsAnalyticsHint: 'Dashboards with reliable information.',
      benefitsImprovementAlt: 'Continuous improvement',
      benefitsImprovementLabel: 'Continuous improvement',
      benefitsImprovementHint: 'The evolution of your company and processes.',
      benefitsTimeAlt: 'Time is gold',
      benefitsTimeLabel: 'Time is gold',
      benefitsTimeHint: 'Invest in technology that eliminates labor waste.',
      promoTag: 'Promotion',
      promoTitle: 'Exclusive Rebirth package',
      promoImageLabel: 'Your passion for success is reborn here',
      promoImageHint: 'Give your company the engine that delivers the outcomes you dream about today.',
      promoList: ['Full process audit', 'Management software (CRM / Automation)', 'Custom workflow design', 'Guided implementation', 'Initial training'],
      conditionTag: 'Special condition',
      conditionDesc: 'One package. One process. One goal: help your company be reborn.',
      whyNowTag: 'Why now',
      whyNowTitle: 'Rebirth is not optional',
      whyNowDesc: 'Future companies will not displace traditional ones. Digitized companies will displace those that do not start today.',
      finalTitle: 'It is time to be reborn',
      finalDesc: 'Request your Rebirth diagnostic and discover how to transform your company from the root.',
      finalLine: 'When technology stops being a cost and becomes power.',
      contactAdvisor: 'Contact an advisor',
    },
    pt: {
      editorialLine: 'Linha de transformacao editorial',
      comparisonLabel: 'Comparacao',
      comparisonTitle: 'Antes vs Depois do Renascimento',
      stageLabel: 'Etapa',
      beforeAfterDrag: 'Arraste horizontalmente para revelar a transicao.',
      beforeAfterAria: 'Deslizar comparacao antes e depois',
      beforeTitle: 'Operacao na penumbra',
      afterTitle: 'Clareza e controle',
      beforeList: ['Informacao dispersa', 'Dependencia de pessoas', 'Erros manuais', 'Decisoes lentas', 'Crescimento desordenado'],
      afterList: ['Informacao centralizada', 'Processos automatizados', 'Rastreabilidade total', 'Decisoes rapidas', 'Escalabilidade controlada'],
      transformationTag: 'Transformacao',
      transformationTitle: 'Do caos ao nucleo',
      transformationSources: ['WhatsApp', 'Excel', 'Correo', 'Chamadas', 'Silos'],
      transformationCoreTitle: 'CRM / Plataforma',
      transformationCoreSubtitle: 'nucleo central',
      transformationOutputs: ['KPIs', 'Alertas', 'Rastreabilidade'],
      transformationFooters: ['Blocos desconectados', 'Nucleo central', 'Decisao com dados'],
      maturityTag: 'Maturidade',
      maturityTitle: 'Sinais de falsa digitalizacao',
      maturityItems: ['Sistemas que nao se falam entre si', 'Carga dupla ou tripla de informacao', 'Relatorios manuais', 'Automacoes sem logica de negocio', 'Tecnologia que atrapalha em vez de ajudar'],
      maturityActionsTitle: 'Acoes imediatas',
      maturityActionsDesc: 'Passe o mouse sobre cada sinal e detecte onde integrar, automatizar e medir primeiro.',
      maturityActionChips: ['Integrar sistemas', 'Eliminar carga duplicada', 'Automatizar relatorios', 'Conectar KPIs'],
      supportImage1Alt: 'Construa bases seguras',
      supportImage1Label: 'Construa bases seguras',
      supportImage1Hint: 'A fortaleza de uma organizacao nao e o marketing, mas o que realmente existe por tras da operacao, ordem e controle de custos.',
      supportImage2Alt: 'Voce sofre os sintomas do caos',
      supportImage2Label: 'Voce sofre os sintomas do caos',
      supportImage2Hint: 'Nao basta ter controle se sua equipe ainda avanca no ritmo da desordem, da baixa rastreabilidade e do retrabalho constante.',
      diagnosisImageAlt: 'Palavras que organizam processos',
      diagnosisImageLabel: 'Palavras que organizam processos',
      diagnosisImageHint: 'Reunir-se com a equipe para ajustar em vez de apenas consertar e algo que poucas empresas realmente fazem.',
      casesTag: 'Casos reais',
      casesTitle: 'Resultados mensuraveis do renascimento',
      casesDesc: 'Nao sao promessas. Sao impactos em conversao, tempo, reclamacoes e experiencia.',
      casesHint: 'KPIs de exemplo com base em implementacao guiada',
      case1: 'Empresa B2B de servicos',
      case2: 'Alto volume de suporte',
      before: 'Antes',
      after: 'Depois',
      case1Before: ['Vendas por WhatsApp pessoal', 'Follow-up manual'],
      case1After: ['CRM + automatizacoes', 'Follow-up automatico', 'KPIs'],
      case2Before: ['Tickets perdidos', 'Reclamacoes repetidas'],
      case2After: ['Contact Center integrado', 'Historico unico', 'KPIs'],
      case1Tag: 'Caso 1',
      case2Tag: 'Caso 2',
      case1ImageAlt: 'Imagem sugerida Caso 1',
      case2ImageAlt: 'Imagem sugerida Caso 2',
      case1ConversionLabel: 'Conversao',
      case1CloseTimeLabel: 'Tempo de fechamento',
      case2ClaimsLabel: 'Reclamacoes',
      case2CsatLabel: 'CSAT',
      stages: [
        {
          title: 'Despertar',
          subtitle: 'Auditoria de processos',
          points: ['Vendas', 'Marketing', 'Atendimento ao cliente', 'Operacoes', 'Direcao'],
          note: 'Nada teorico. Tudo real.',
          visualAlt: 'Imagem sugerida para Despertar',
          visualHint: 'Mapa de processos em papel com escritorio premium e luz quente.',
        },
        {
          title: 'Purificacao',
          subtitle: 'Eliminacao de friccao',
          points: ['Processos redundantes', 'Etapas manuais desnecessarias', 'Dependencias criticas'],
          note: 'Aqui morre o que nao agrega valor.',
          visualAlt: 'Imagem sugerida para Purificacao',
          visualHint: 'Checklist minimalista, destaque dourado e foco em eliminar.',
        },
        {
          title: 'Reconstrucao',
          subtitle: 'Estrategia',
          points: ['CRM', 'Automatizacoes', 'Fluxos inteligentes', 'Canais centralizados'],
          note: 'Nem tudo e digitalizado. Apenas o que impacta resultados.',
          visualAlt: 'Imagem sugerida para Reconstrucao',
          visualHint: 'Diagrama de fluxo limpo conectando canais a um nucleo central.',
        },
        {
          title: 'Iluminacao',
          subtitle: 'Analitica e controle',
          points: ['KPIs claros', 'Dashboards em tempo real', 'Alertas automaticos'],
          note: 'A empresa deixa de sentir e comeca a ver.',
          visualAlt: 'Imagem sugerida para Iluminacao',
          visualHint: 'Dashboard premium escuro com graficos dourados e alertas sutis.',
        },
        {
          title: 'Renascer',
          subtitle: 'Melhoria continua',
          points: ['Processos vivos', 'Automatizacoes evolutivas', 'Decisoes baseadas em dados'],
          note: 'A empresa renasce preparada para crescer.',
          visualAlt: 'Imagem sugerida para Renascer',
          visualHint: 'Luz ascendente, skyline minimalista e sensacao de expansao controlada.',
        },
      ],
      benefitsTag: 'Beneficios',
      benefitsTitle: 'O exclusivo nao e o software. E o controle.',
      benefitsDesc: 'Controle total do negocio, menos dependencia operacional, mais produtividade sem mais pessoas, dados confiaveis e escalabilidade real.',
      benefitsList: ['Controle total do negocio', 'Menos dependencia operacional', 'Mais produtividade sem mais pessoas', 'Dados confiaveis', 'Escalabilidade real', 'Imagem corporativa moderna e solida'],
      benefitsAnalyticsAlt: 'Analitica',
      benefitsAnalyticsLabel: 'Analitica',
      benefitsAnalyticsHint: 'Dashboards com informacoes confiaveis.',
      benefitsImprovementAlt: 'Melhoria continua',
      benefitsImprovementLabel: 'Melhoria continua',
      benefitsImprovementHint: 'A evolucao da sua empresa e dos seus processos.',
      benefitsTimeAlt: 'Tempo e ouro',
      benefitsTimeLabel: 'Tempo e ouro',
      benefitsTimeHint: 'Invista em tecnologia que elimine desperdicio operacional.',
      promoTag: 'Promocao',
      promoTitle: 'Pacote exclusivo Renascer',
      promoImageLabel: 'Aqui renasce sua paixao pelo sucesso',
      promoImageHint: 'Dê a sua empresa o motor que entrega os resultados com que voce sonha.',
      promoList: ['Auditoria completa de processos', 'Software de gestao (CRM / Automacao)', 'Desenho de fluxos personalizados', 'Implementacao guiada', 'Treinamento inicial'],
      conditionTag: 'Condicao especial',
      conditionDesc: 'Um pacote. Um processo. Um objetivo: fazer sua empresa renascer.',
      whyNowTag: 'Por que agora',
      whyNowTitle: 'Renascer nao e opcional',
      whyNowDesc: 'Empresas digitalizadas vao superar as que nao comecarem hoje.',
      finalTitle: 'E hora de Renascer',
      finalDesc: 'Solicite seu diagnostico Renascimento e descubra como transformar sua empresa desde a raiz.',
      finalLine: 'Quando a tecnologia deixa de ser custo e vira poder.',
      contactAdvisor: 'Falar com um consultor',
    },
  }[lang] || {};
  const caseStatsRef = useRef(null);
  const statsInView = useInView(caseStatsRef, { threshold: 0.35, rootMargin: '80px' });

  const conversion = useCountUp(statsInView, 32);
  const closeTime = useCountUp(statsInView, 40);
  const claims = useCountUp(statsInView, 45);
  const csat = useCountUp(statsInView, 27);

  const stages = useMemo(
    () => (c.stages || []).map((stage, idx) => ({
      ...stage,
      icon: [
        <Sparkles className="w-5 h-5 text-[#d4af37]" />,
        <Flame className="w-5 h-5 text-[#d4af37]" />,
        <Workflow className="w-5 h-5 text-[#d4af37]" />,
        <LineChart className="w-5 h-5 text-[#d4af37]" />,
        <Crown className="w-5 h-5 text-[#d4af37]" />,
      ][idx],
      visualSrc: [
        '/images/renacimiento/renac_4.png',
        '/images/renacimiento/renac_5.png',
        '/images/renacimiento/renac_6.png',
        '/images/renacimiento/renac_7.png',
        '/images/renacimiento/renac_8.png',
      ][idx],
    })),
    [c]
  );

  const [activeStageIndex, setActiveStageIndex] = useState(0);

  useEffect(() => {
    const nodes = Array.from(document.querySelectorAll('[data-stage-index]'));
    if (!nodes.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0))[0];
        if (!visible) return;
        const idx = Number(visible.target.getAttribute('data-stage-index'));
        if (Number.isFinite(idx)) setActiveStageIndex(idx);
      },
      { threshold: [0.35, 0.55, 0.7], rootMargin: '-20% 0px -55% 0px' }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <PageHeader />

      <main className="pt-20">
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_0%,rgba(212,175,55,0.25),rgba(0,0,0,0)_70%)]" />
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[64rem] h-[64rem] rounded-full bg-[#d4af37]/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/35 to-transparent" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 relative">
            <div className="flex items-center justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/25 bg-black/40 px-4 py-2 text-xs uppercase tracking-[0.35em] text-white/70">
                <Wand2 className="w-4 h-4 text-[#d4af37]" />
                {t('renacimiento.hero.tag')}
              </div>
            </div>

            <h1 className="mt-7 text-center font-serif text-4xl md:text-6xl font-bold tracking-tight">
              {t('renacimiento.hero.title')}
            </h1>
            <p className="mt-6 text-center text-white/70 max-w-3xl mx-auto text-lg leading-relaxed">
              {t('renacimiento.hero.subtitle')}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={WHATSAPP_RENACIMIENTO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#d4af37] text-black font-semibold hover:bg-[#e1c05a] transition"
              >
                {t('renacimiento.hero.cta')} <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="mt-14 grid md:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-0.5 hover:border-[#d4af37]/25 hover:bg-white/10">
                <div className="text-xs uppercase tracking-[0.25em] text-white/60">{t('renacimiento.intro.card1.tag')}</div>
                <div className="mt-3 font-serif text-xl">{t('renacimiento.intro.card1.title')}</div>
                <p className="mt-3 text-white/70 leading-relaxed">
                  {t('renacimiento.intro.card1.desc')}
                </p>
              </div>
              <div className="rounded-2xl border border-[#d4af37]/25 bg-black/35 p-6 transition hover:-translate-y-0.5 hover:border-[#d4af37]/45 hover:bg-black/45">
                <div className="text-xs uppercase tracking-[0.25em] text-white/60">{t('renacimiento.intro.card2.tag')}</div>
                <div className="mt-3 font-serif text-xl">{t('renacimiento.intro.card2.title')}</div>
                <p className="mt-3 text-white/70 leading-relaxed">
                  {t('renacimiento.intro.card2.desc')}
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 transition hover:-translate-y-0.5 hover:border-[#d4af37]/25 hover:bg-white/10">
                <div className="text-xs uppercase tracking-[0.25em] text-white/60">{t('renacimiento.intro.card3.tag')}</div>
                <ul className="mt-4 space-y-2 text-white/75">
                  {t('renacimiento.intro.card3.points', { returnObjects: true }).map((point, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <ScrollReveal className="mt-10">
              <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-7 grid gap-6">
                  <TransformationDiagram
                    tag={c.transformationTag}
                    title={c.transformationTitle}
                    sourceNodes={[
                      { x: 120, y: 70, label: c.transformationSources[0] },
                      { x: 120, y: 140, label: c.transformationSources[1] },
                      { x: 120, y: 210, label: c.transformationSources[2] },
                      { x: 300, y: 100, label: c.transformationSources[3] },
                      { x: 300, y: 180, label: c.transformationSources[4] },
                    ]}
                    coreTitle={c.transformationCoreTitle}
                    coreSubtitle={c.transformationCoreSubtitle}
                    outputNodes={[
                      { x: 740, y: 95, label: c.transformationOutputs[0] },
                      { x: 740, y: 155, label: c.transformationOutputs[1] },
                      { x: 740, y: 215, label: c.transformationOutputs[2] },
                    ]}
                    footerCards={c.transformationFooters}
                  />
                  <LuxeImageSlot
                    src="/images/renacimiento/renac_2.png"
                    alt={c.supportImage1Alt}
                    label={c.supportImage1Label}
                    hint={c.supportImage1Hint}
                  />
                </div>
                <div className="lg:col-span-5 grid gap-6">
                  <LuxeImageSlot
                    src="/images/renacimiento/renac_1.png"
                    alt={c.supportImage2Alt}
                    label={c.supportImage2Label}
                    hint={c.supportImage2Hint}
                    aspect="aspect-[4/3]"
                  />
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-7 transition hover:border-[#d4af37]/25 hover:bg-white/10">
                    <div className="text-xs uppercase tracking-[0.35em] text-white/60">{t('renacimiento.atmosphere.tag')}</div>
                    <div className="mt-2 font-serif text-xl">{t('renacimiento.atmosphere.title')}</div>
                    <div className="mt-4 grid gap-3 text-sm">
                      <div className="rounded-xl border border-white/10 bg-black/30 px-4 py-3 flex items-center gap-3 transition hover:border-[#d4af37]/25 hover:bg-black/40 hover:-translate-y-0.5">
                        <Mail className="w-4 h-4 text-white/60" />
                        {t('renacimiento.atmosphere.silos')}
                      </div>
                      <div className="rounded-xl border border-white/10 bg-black/30 px-4 py-3 flex items-center gap-3 transition hover:border-[#d4af37]/25 hover:bg-black/40 hover:-translate-y-0.5">
                        <PhoneCall className="w-4 h-4 text-white/60" />
                        {t('renacimiento.atmosphere.decisions')}
                      </div>
                      <div className="rounded-xl border border-[#d4af37]/25 bg-black/35 px-4 py-3 flex items-center gap-3 transition hover:border-[#d4af37]/45 hover:bg-black/45 hover:-translate-y-0.5">
                        <Database className="w-4 h-4 text-[#d4af37]" />
                        {t('renacimiento.atmosphere.core')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="bg-[#0b0b0b] border-y border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-18">
            <div className="max-w-3xl">
              <div className="text-xs uppercase tracking-[0.35em] text-white/60">{t('renacimiento.diagnosis.tag')}</div>
              <h2 className="mt-4 font-serif text-3xl md:text-4xl">{t('renacimiento.diagnosis.title')}</h2>
              <p className="mt-5 text-white/70 leading-relaxed">
                {t('renacimiento.diagnosis.desc')}
              </p>
            </div>

            <div className="mt-10 grid md:grid-cols-2 gap-6 items-stretch">
              <ScrollReveal className="h-full">
                <MaturityBar
                  className="h-full"
                  titleTag={c.maturityTag}
                  title={c.maturityTitle}
                  items={c.maturityItems}
                  actionsTitle={c.maturityActionsTitle}
                  actionsDesc={c.maturityActionsDesc}
                  actionChips={c.maturityActionChips}
                />
              </ScrollReveal>

              <div className="rounded-2xl border border-[#d4af37]/25 bg-black/35 p-7 flex flex-col h-full transition hover:border-[#d4af37]/40 hover:bg-black/40">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-white/60">{t('renacimiento.diagnosis.audit.tag')}</div>
                  <p className="mt-5 text-white/70 leading-relaxed">
                    {t('renacimiento.diagnosis.audit.desc')}
                  </p>
                </div>
                <div className="mt-8 grid gap-3">
                  <LuxeImageSlot
                    src="/images/renacimiento/renac_3.png"
                    alt={c.diagnosisImageAlt}
                    label={c.diagnosisImageLabel}
                    hint={c.diagnosisImageHint}
                  />
                </div>
                <div className="mt-6 grid sm:grid-cols-3 gap-3">
                  {t('renacimiento.diagnosis.items', { returnObjects: true }).map((it) => (
                    <div
                      key={it.title}
                      className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 transition hover:border-[#d4af37]/25 hover:bg-black/30"
                    >
                      <div className="text-xs uppercase tracking-[0.25em] text-white/70">{it.title}</div>
                      <div className="mt-2 text-white/65 text-sm leading-snug">{it.desc}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-auto pt-8">
                  <a
                    href={WHATSAPP_RENACIMIENTO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] text-black px-6 py-3 font-semibold hover:bg-[#e1c05a] transition"
                  >
                    {t('renacimiento.hero.cta')} <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div className="max-w-3xl">
                <div className="text-xs uppercase tracking-[0.35em] text-white/60">{t('renacimiento.process.tag')}</div>
                <h2 className="mt-4 font-serif text-3xl md:text-4xl">{t('renacimiento.process.title')}</h2>
                <p className="mt-5 text-white/70 leading-relaxed">
                  {t('renacimiento.process.desc')}
                </p>
              </div>
              <div className="hidden md:block text-white/55 text-sm tracking-wide">{c.editorialLine}</div>
            </div>

            <ScrollReveal className="mt-10">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 md:p-6">
                <div className="flex items-center justify-between gap-6 flex-wrap">
                  <div className="text-xs uppercase tracking-[0.35em] text-white/60">Timeline</div>
                  <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.stageLabel} {activeStageIndex + 1}/5</div>
                </div>
                <div className="mt-4 flex items-center gap-3 overflow-x-auto pb-1">
                  {stages.map((s, idx) => (
                    <button
                      key={s.title}
                      onClick={() => {
                        const el = document.querySelector(`[data-stage-index="${idx}"]`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                      className={`shrink-0 rounded-full border px-4 py-2 text-xs uppercase tracking-[0.25em] transition ${
                        idx === activeStageIndex
                          ? 'border-[#d4af37]/40 bg-black/35 text-white'
                          : 'border-white/10 bg-black/20 text-white/65 hover:text-white/85 hover:bg-white/5'
                      }`}
                    >
                      {s.title}
                    </button>
                  ))}
                </div>
                <div className="mt-6 grid md:grid-cols-5 gap-4">
                  {stages.map((s, idx) => (
                    <div
                      key={s.title}
                      className={`group rounded-2xl border overflow-hidden transition hover:-translate-y-0.5 ${
                        idx === activeStageIndex ? 'border-[#d4af37]/35 bg-black/35' : 'border-white/10 bg-black/20'
                      }`}
                    >
                      <div className="relative aspect-square">
                        <img
                          src={s.visualSrc}
                          alt={s.visualAlt}
                          className="absolute inset-0 w-full h-full object-cover opacity-80 transition duration-700 group-hover:scale-[1.03] group-hover:opacity-95"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://placehold.co/1080x1080/0b0b0b/d4af37?text=Visual';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        <div className="absolute left-4 bottom-4 text-xs uppercase tracking-[0.35em] text-white/80">
                          {c.stageLabel} {idx + 1}
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="font-serif text-lg text-white">{s.subtitle}</div>
                        <div className="mt-2 text-white/60 text-xs leading-relaxed">{s.visualHint}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <div className="mt-10 grid lg:grid-cols-5 gap-4">
              {stages.map((s, idx) => (
                <div
                  key={s.title}
                  data-stage-index={idx}
                  className={`rounded-2xl border p-6 transition hover:-translate-y-0.5 hover:border-[#d4af37]/25 hover:bg-black/35 ${
                    idx === 4 ? 'border-[#d4af37]/30 bg-black/35' : 'border-white/10 bg-white/5'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-1 text-xs uppercase tracking-[0.25em] text-white/70">
                      {s.icon}
                      {c.stageLabel} {idx + 1}
                    </div>
                    <div className={`w-2.5 h-2.5 rounded-full ${idx === activeStageIndex ? 'bg-[#d4af37]' : 'bg-white/20'}`} />
                  </div>
                  <div className="mt-4 font-serif text-xl">{s.title}</div>
                  <div className="mt-1 text-white/60 text-sm">{s.subtitle}</div>
                  <ul className="mt-5 space-y-2 text-white/75 text-sm">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 text-white/60 text-sm leading-relaxed">{s.note}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#0b0b0b] border-y border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <ScrollReveal className="mb-8">
              <BeforeAfterSlider
                beforeSrc="/images/renacimiento/renac_9B.png"
                afterSrc="/images/renacimiento/renac_9A.png"
                beforeLabel={c.before}
                afterLabel={c.after}
                comparisonLabel={c.comparisonLabel}
                title={c.comparisonTitle}
                dragHint={c.beforeAfterDrag}
                ariaLabel={c.beforeAfterAria}
              />
            </ScrollReveal>
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="rounded-2xl border border-white/10 bg-black/40 overflow-hidden transition hover:-translate-y-0.5 hover:border-[#d4af37]/25 hover:bg-black/50">
                <div className="px-7 py-5 border-b border-white/10">
                  <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.before}</div>
                  <div className="mt-2 font-serif text-2xl">{c.beforeTitle}</div>
                </div>
                <div className="p-7">
                  <ul className="space-y-3 text-white/75">
                    {(c.beforeList || []).map((t) => (
                      <li key={t} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-white/30" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="rounded-2xl border border-[#d4af37]/25 bg-gradient-to-b from-black/35 to-black/10 overflow-hidden transition hover:-translate-y-0.5 hover:border-[#d4af37]/45">
                <div className="px-7 py-5 border-b border-[#d4af37]/15">
                  <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.after}</div>
                  <div className="mt-2 font-serif text-2xl">{c.afterTitle}</div>
                </div>
                <div className="p-7">
                  <ul className="space-y-3 text-white/80">
                    {(c.afterList || []).map((t) => (
                      <li key={t} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <div className="flex items-end justify-between flex-wrap gap-6">
              <div className="max-w-3xl">
                <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.casesTag}</div>
                <h2 className="mt-4 font-serif text-3xl md:text-4xl">{c.casesTitle}</h2>
                <p className="mt-5 text-white/70 leading-relaxed">
                  {c.casesDesc}
                </p>
              </div>
              <div className="text-white/60 text-sm">{c.casesHint}</div>
            </div>

            <div ref={caseStatsRef} className="mt-12 grid lg:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden transition hover:-translate-y-0.5 hover:border-[#d4af37]/25 hover:bg-white/10">
                <div className="px-7 py-6 border-b border-white/10">
                  <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.case1Tag}</div>
                  <div className="mt-2 font-serif text-2xl">{c.case1}</div>
                </div>
                <div className="p-7 grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <div className="group relative aspect-[21/9] rounded-2xl overflow-hidden border border-white/10 bg-black/40">
                      <img
                        src="/images/renacimiento/renac_10.png"
                        alt={c.case1ImageAlt}
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 transition duration-700 group-hover:scale-[1.03]"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/1600x686/0b0b0b/d4af37?text=Caso+1';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-white/60">{c.before}</div>
                    <ul className="mt-4 space-y-2 text-white/75 text-sm">
                      {(c.case1Before || []).map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-white/30" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-white/60">{c.after}</div>
                    <ul className="mt-4 space-y-2 text-white/80 text-sm">
                      {(c.case1After || []).map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
                    <StatCard label={c.case1ConversionLabel} value={conversion} suffix="%" tone="gold" />
                    <StatCard label={c.case1CloseTimeLabel} value={closeTime} suffix="%" tone="neutral" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[#d4af37]/25 bg-black/35 overflow-hidden transition hover:-translate-y-0.5 hover:border-[#d4af37]/45 hover:bg-black/40">
                <div className="px-7 py-6 border-b border-[#d4af37]/15">
                  <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.case2Tag}</div>
                  <div className="mt-2 font-serif text-2xl">{c.case2}</div>
                </div>
                <div className="p-7 grid md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <div className="group relative aspect-[21/9] rounded-2xl overflow-hidden border border-white/10 bg-black/40">
                      <img
                        src="/images/renacimiento/renac_11.png"
                        alt={c.case2ImageAlt}
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 transition duration-700 group-hover:scale-[1.03]"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/1600x686/0b0b0b/d4af37?text=Caso+2';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-white/60">{c.before}</div>
                    <ul className="mt-4 space-y-2 text-white/75 text-sm">
                      {(c.case2Before || []).map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-white/30" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-white/60">{c.after}</div>
                    <ul className="mt-4 space-y-2 text-white/80 text-sm">
                      {(c.case2After || []).map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
                    <StatCard label={c.case2ClaimsLabel} value={claims} suffix="%" tone="neutral" />
                    <StatCard label={c.case2CsatLabel} value={csat} suffix="%" tone="gold" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#0b0b0b] border-y border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <div className="grid lg:grid-cols-12 gap-8 items-stretch">
              <div className="lg:col-span-7 h-full flex flex-col">
                <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.benefitsTag}</div>
                <h2 className="mt-4 font-serif text-3xl md:text-4xl">{c.benefitsTitle}</h2>
                <p className="mt-5 text-white/70 leading-relaxed">
                  {c.benefitsDesc}
                </p>
                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  {(c.benefitsList || []).map((b) => (
                    <div
                      key={b}
                      className="rounded-xl border border-white/10 bg-white/5 px-5 py-4 text-white/80 transition hover:-translate-y-0.5 hover:border-[#d4af37]/25 hover:bg-black/35"
                    >
                      {b}
                    </div>
                  ))}
                </div>
                <ScrollReveal className="mt-10">
                  <div className="grid md:grid-cols-3 gap-4">
                    <LuxeImageSlot
                      src="/images/renacimiento/renac_13.png"
                      alt={c.benefitsAnalyticsAlt}
                      label={c.benefitsAnalyticsLabel}
                      hint={c.benefitsAnalyticsHint}
                    />
                    <LuxeImageSlot
                      src="/images/renacimiento/renac_14.png"
                      alt={c.benefitsImprovementAlt}
                      label={c.benefitsImprovementLabel}
                      hint={c.benefitsImprovementHint}
                    />
                    <LuxeImageSlot
                      src="/images/renacimiento/renac_15.png"
                      alt={c.benefitsTimeAlt}
                      label={c.benefitsTimeLabel}
                      hint={c.benefitsTimeHint}
                    />
                  </div>
                </ScrollReveal>
              </div>

              <div className="lg:col-span-5 h-full">
                <div className="rounded-2xl border border-[#d4af37]/25 bg-black/35 overflow-hidden h-full flex flex-col transition hover:border-[#d4af37]/45 hover:bg-black/40">
                  <div className="px-7 py-6 border-b border-[#d4af37]/15">
                    <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.promoTag}</div>
                    <div className="mt-2 font-serif text-2xl">{c.promoTitle}</div>
                  </div>
                  <div className="p-7 flex flex-col h-full">
                    <div className="mb-6">
                      <LuxeImageSlot
                        src="/images/renacimiento/renac_12.png"
                        alt={c.promoImageLabel}
                        label={c.promoImageLabel}
                        hint={c.promoImageHint}
                      />
                    </div>
                    <ul className="space-y-3 text-white/80">
                      {(c.promoList || []).map((it) => (
                        <li key={it} className="flex items-start gap-3">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#d4af37]" />
                          {it}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 rounded-xl border border-white/10 bg-white/5 p-5 transition hover:border-[#d4af37]/25 hover:bg-black/35">
                      <div className="text-xs uppercase tracking-[0.25em] text-white/60">{c.conditionTag}</div>
                      <div className="mt-2 text-white/80 leading-relaxed">
                        {c.conditionDesc}
                      </div>
                    </div>
                    <div className="mt-auto pt-8">
                      <a
                        href={WHATSAPP_RENACIMIENTO_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] text-black px-6 py-3 font-semibold hover:bg-[#e1c05a] transition"
                      >
                        {c.contactAdvisor} <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-black">
          <div className="absolute inset-0 bg-[radial-gradient(60%_40%_at_50%_100%,rgba(212,175,55,0.2),rgba(0,0,0,0)_70%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 relative">
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-xs uppercase tracking-[0.35em] text-white/60">{c.whyNowTag}</div>
              <h2 className="mt-4 font-serif text-3xl md:text-5xl">{c.whyNowTitle}</h2>
              <p className="mt-6 text-white/70 text-lg leading-relaxed">
                {c.whyNowDesc}
              </p>
              <div className="mt-10 rounded-2xl border border-[#d4af37]/25 bg-black/35 px-8 py-10">
                <div className="font-serif text-2xl md:text-3xl">{c.finalTitle}</div>
                <p className="mt-4 text-white/70 leading-relaxed">
                  {c.finalDesc}
                </p>
                <a
                  href={WHATSAPP_RENACIMIENTO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-[#d4af37] text-black px-7 py-3 font-semibold hover:bg-[#e1c05a] transition"
                >
                  {c.contactAdvisor} <ArrowRight className="w-4 h-4" />
                </a>
                <div className="mt-6 text-white/55">
                  {c.finalLine}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RenacimientoPage;
